import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Rnd } from "react-rnd";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { FaTrash } from "react-icons/fa";
import { languageHelper } from "../../../../config/helperFunctions";
import {
  FIELD_PALETTE,
  FIELD_TYPES,
  CANVAS_WIDTH_PX,
  sampleInvoiceData,
  resolveFieldValue,
  getPaletteEntry,
} from "./invoiceFieldPalette";
import { getTemplate, saveTemplate } from "./invoiceTemplateApi";

const CANVAS_HEIGHT_PX = 900;
const SNAP_THRESHOLD = 6; // px — how close before a field snaps + shows a guide line

// Compares the dragged box's left/center/right (and top/center/bottom) against
// the canvas edges/center and every other field's edges/center. Returns the
// snapped x/y (unchanged if nothing is within SNAP_THRESHOLD) plus the guide
// line positions to render.
function computeAlignment(draggedId, x, y, width, height, allFields) {
  const others = allFields.filter((f) => f.id !== draggedId);

  const vTargets = [0, CANVAS_WIDTH_PX, CANVAS_WIDTH_PX / 2];
  const hTargets = [0];
  others.forEach((f) => {
    vTargets.push(f.x, f.x + f.width, f.x + f.width / 2);
    hTargets.push(f.y, f.y + f.height, f.y + f.height / 2, f.y + f.height);
  });

  const ownX = [
    { edge: x, delta: 0 },
    { edge: x + width / 2, delta: width / 2 },
    { edge: x + width, delta: width },
  ];
  const ownY = [
    { edge: y, delta: 0 },
    { edge: y + height / 2, delta: height / 2 },
    { edge: y + height, delta: height },
  ];

  let snappedX = x;
  let bestXDiff = SNAP_THRESHOLD + 1;
  const vGuides = new Set();
  ownX.forEach(({ edge, delta }) => {
    vTargets.forEach((target) => {
      const diff = Math.abs(edge - target);
      if (diff <= SNAP_THRESHOLD) {
        vGuides.add(target);
        if (diff < bestXDiff) {
          bestXDiff = diff;
          snappedX = target - delta;
        }
      }
    });
  });

  let snappedY = y;
  let bestYDiff = SNAP_THRESHOLD + 1;
  const hGuides = new Set();
  ownY.forEach(({ edge, delta }) => {
    hTargets.forEach((target) => {
      const diff = Math.abs(edge - target);
      if (diff <= SNAP_THRESHOLD) {
        hGuides.add(target);
        if (diff < bestYDiff) {
          bestYDiff = diff;
          snappedY = target - delta;
        }
      }
    });
  });

  return {
    x: Math.round(snappedX),
    y: Math.round(snappedY),
    vGuides: [...vGuides],
    hGuides: [...hGuides],
  };
}

function makeFieldInstance(paletteKey, x, y) {
  const entry = getPaletteEntry(paletteKey);
  if (!entry) return null;
  return {
    id: uuidv4(),
    key: entry.key,
    type: entry.type,
    path: entry.path,
    label: entry.label,
    text: entry.defaultText || "",
    x: Math.max(0, Math.round(x)),
    y: Math.max(0, Math.round(y)),
    width: entry.defaultSize.width,
    height: entry.defaultSize.height,
    style: { ...entry.defaultStyle },
  };
}

function FieldContent({ field }) {
  const style = {
    fontSize: field.style?.fontSize || 12,
    fontWeight: field.style?.bold ? "bold" : "normal",
    textAlign: field.style?.align || "left",
    width: "100%",
    height: "100%",
    overflow: "hidden",
  };

  switch (field.type) {
    case FIELD_TYPES.LOGO:
      return (
        <div
          style={style}
          className="flex items-center justify-center border border-dashed border-gray-400 text-gray-400 text-xs"
        >
          {languageHelper("Logo")}
        </div>
      );
    case FIELD_TYPES.QR: {
      const src = resolveFieldValue(
        { ...field, type: FIELD_TYPES.DATA },
        sampleInvoiceData
      );
      const isBoundValue = src.startsWith("data:image");
      return isBoundValue ? (
        <img src={src} alt="QR" style={{ width: "100%", height: "100%" }} />
      ) : (
        <div
          style={style}
          className="flex items-center justify-center border border-dashed border-gray-400 text-gray-400 text-xs"
        >
          {languageHelper("QR Code")}
        </div>
      );
    }
    case FIELD_TYPES.DIVIDER:
      return (
        <div
          style={{ width: "100%", height: "100%" }}
          className="flex items-center"
        >
          <hr style={{ width: "100%", margin: 0, border: "none", borderTop: "1px solid #333" }} />
        </div>
      );
    case FIELD_TYPES.ITEMS_TABLE:
      return (
        <table style={{ ...style, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #333" }}>
              <th style={{ textAlign: "left" }}>{languageHelper("Name")}</th>
              <th>{languageHelper("Qty")}</th>
              <th>{languageHelper("Price")}</th>
              <th>{languageHelper("Vat")}</th>
              <th>{languageHelper("Total")}</th>
            </tr>
          </thead>
          <tbody>
            {sampleInvoiceData.data.order_details.map((item, idx) => (
              <tr key={idx}>
                <td style={{ textAlign: "left" }}>{item.name}</td>
                <td style={{ textAlign: "center" }}>{item.qty}</td>
                <td style={{ textAlign: "center" }}>{item.price.toFixed(2)}</td>
                <td style={{ textAlign: "center" }}>{item.vat.toFixed(2)}</td>
                <td style={{ textAlign: "center" }}>{item.total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    case FIELD_TYPES.TOTALS: {
      const d = sampleInvoiceData.data;
      const rows = [
        [languageHelper("Subtotal"), d.sub_total.toFixed(2)],
        [languageHelper("Discount"), d.discount.toFixed(2)],
        [languageHelper("Vat"), d.vat_total.toFixed(2)],
        [languageHelper("Total"), d.total_payable.toFixed(2)],
      ];
      return (
        <div style={{ fontSize: style.fontSize, width: "100%" }}>
          {rows.map(([label, value]) => (
            <div key={label} className="flex justify-between">
              <span>{label}</span>
              <span>{value}</span>
            </div>
          ))}
        </div>
      );
    }
    case FIELD_TYPES.TEXT:
      return <div style={style}>{field.text || languageHelper("Custom text")}</div>;
    case FIELD_TYPES.DATA:
    default:
      return <div style={style}>{resolveFieldValue(field, sampleInvoiceData)}</div>;
  }
}

function InvoiceBuilderEditor() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [name, setName] = useState("");
  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [guideLines, setGuideLines] = useState({ vGuides: [], hGuides: [] });
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    getTemplate(user, id).then(({ data }) => {
      if (data) {
        setName(data.name || "");
        setFields(
          typeof data.fields === "string"
            ? JSON.parse(data.fields || "[]")
            : data.fields ||
                (data.layout ? JSON.parse(data.layout) : [])
        );
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addFieldAt = useCallback((paletteKey, x, y) => {
    const instance = makeFieldInstance(paletteKey, x, y);
    if (!instance) return;
    setFields((prev) => [...prev, instance]);
    setSelectedId(instance.id);
  }, []);

  const handlePaletteDragStart = (e, paletteKey) => {
    e.dataTransfer.setData("text/palette-key", paletteKey);
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const paletteKey = e.dataTransfer.getData("text/palette-key");
    if (!paletteKey || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    addFieldAt(paletteKey, e.clientX - rect.left, e.clientY - rect.top);
  };

  const handleCanvasDragOver = (e) => e.preventDefault();

  const updateField = (fieldId, patch) => {
    setFields((prev) =>
      prev.map((f) => (f.id === fieldId ? { ...f, ...patch } : f))
    );
  };

  // Live drag: only update the (separate) guide-line state so the pink
  // lines show up as you move. We deliberately do NOT touch `fields` here —
  // Rnd/react-draggable already tracks the drag smoothly on its own, and
  // forcing its controlled position/size to a snapped value on every
  // mousemove fights that internal tracking and makes the box visibly
  // judder. The snap itself is only applied once, on release.
  const handleDrag = (field, data) => {
    const snap = computeAlignment(
      field.id,
      data.x,
      data.y,
      field.width,
      field.height,
      fields
    );
    setGuideLines({ vGuides: snap.vGuides, hGuides: snap.hGuides });
  };

  const handleDragStop = (fieldId, data) => {
    setGuideLines({ vGuides: [], hGuides: [] });
    setFields((prev) => {
      const field = prev.find((f) => f.id === fieldId);
      if (!field) return prev;
      const snap = computeAlignment(
        fieldId,
        data.x,
        data.y,
        field.width,
        field.height,
        prev
      );
      return prev.map((f) =>
        f.id === fieldId ? { ...f, x: snap.x, y: snap.y } : f
      );
    });
  };

  const handleResize = (field, ref, position) => {
    const width = parseInt(ref.style.width, 10);
    const height = parseInt(ref.style.height, 10);
    const snap = computeAlignment(
      field.id,
      position.x,
      position.y,
      width,
      height,
      fields
    );
    setGuideLines({ vGuides: snap.vGuides, hGuides: snap.hGuides });
  };

  const handleResizeStop = (fieldId, ref, position) => {
    const width = parseInt(ref.style.width, 10);
    const height = parseInt(ref.style.height, 10);
    setGuideLines({ vGuides: [], hGuides: [] });
    setFields((prev) => {
      const snap = computeAlignment(fieldId, position.x, position.y, width, height, prev);
      return prev.map((f) =>
        f.id === fieldId ? { ...f, x: snap.x, y: snap.y, width, height } : f
      );
    });
  };

  const updateFieldStyle = (fieldId, stylePatch) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId ? { ...f, style: { ...f.style, ...stylePatch } } : f
      )
    );
  };

  const removeField = (fieldId) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
    if (selectedId === fieldId) setSelectedId(null);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Please give this invoice template a name", {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
      });
      return;
    }
    setSaving(true);
    const { source } = await saveTemplate(user, { id, name, fields });
    setSaving(false);
    toast.success(
      source === "server"
        ? "Template saved"
        : "Template saved locally (backend endpoint not available yet)",
      { position: "bottom-right", autoClose: 5000, theme: "colored" }
    );
    navigate(-1);
  };

  const selectedField = fields.find((f) => f.id === selectedId);

  if (loading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <div className="flex items-center gap-3 flex-1 min-w-[240px]">
            <h1 className="text-d-bg-text text-2xl whitespace-nowrap">
              {isEdit
                ? languageHelper("Edit Invoice Template")
                : languageHelper("New Invoice Template")}
            </h1>
            <input
              type="text"
              placeholder={languageHelper("Template Name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline flex-1 max-w-xs"
            />
          </div>
          <div className="flex gap-2">
            <button
              className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none w-fit"
              type="button"
              onClick={handleSave}
              disabled={saving}
            >
              {languageHelper("Save")}
            </button>
            <button
              className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none w-fit"
              type="button"
              onClick={() => navigate(-1)}
            >
              {languageHelper("Back")}
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-500 mb-3">
          {languageHelper(
            "Preview only for now — this is a design/mapping tool. Wiring templates into real printing (JSPM / 80mm silent print) is a separate follow-up step."
          )}
        </p>

        <div className="flex gap-4 items-start flex-wrap xl:flex-nowrap">
          {/* Palette */}
          <div className="w-full xl:w-56 shrink-0 border rounded-lg p-3 bg-gray-50">
            <h3 className="text-sm font-semibold text-d-bg-text mb-2">
              {languageHelper("Fields")}
            </h3>
            <p className="text-xs text-gray-500 mb-2">
              {languageHelper("Drag a field onto the invoice canvas")}
            </p>
            <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1">
              {FIELD_PALETTE.map((entry) => (
                <div
                  key={entry.key}
                  draggable
                  onDragStart={(e) => handlePaletteDragStart(e, entry.key)}
                  onDoubleClick={() => addFieldAt(entry.key, 20, 20)}
                  title={languageHelper("Drag onto the canvas, or double-click to add")}
                  className="px-2 py-1.5 bg-white border rounded cursor-move text-xs text-d-bg-text hover:border-d-cat-btn select-none"
                >
                  {languageHelper(entry.label)}
                </div>
              ))}
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 overflow-x-auto">
            <div className="text-xs text-gray-500 mb-1">
              {languageHelper("80mm printer width")} ({CANVAS_WIDTH_PX}px)
            </div>
            <div
              ref={canvasRef}
              onDrop={handleCanvasDrop}
              onDragOver={handleCanvasDragOver}
              onClick={(e) => {
                if (e.target === canvasRef.current) setSelectedId(null);
              }}
              className="relative bg-white border-2 border-dashed border-gray-300 mx-auto"
              style={{
                width: CANVAS_WIDTH_PX,
                minHeight: CANVAS_HEIGHT_PX,
              }}
            >
              {fields.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-sm px-4 text-center pointer-events-none">
                  {languageHelper("Drag fields here to build the invoice layout")}
                </div>
              )}
              {guideLines.vGuides.map((x) => (
                <div
                  key={`v-${x}`}
                  className="absolute top-0 bg-pink-500 pointer-events-none"
                  style={{ left: x, width: 1, height: CANVAS_HEIGHT_PX, zIndex: 50 }}
                />
              ))}
              {guideLines.hGuides.map((y) => (
                <div
                  key={`h-${y}`}
                  className="absolute left-0 bg-pink-500 pointer-events-none"
                  style={{ top: y, height: 1, width: CANVAS_WIDTH_PX, zIndex: 50 }}
                />
              ))}
              {fields.map((field) => (
                <Rnd
                  key={field.id}
                  bounds="parent"
                  size={{ width: field.width, height: field.height }}
                  position={{ x: field.x, y: field.y }}
                  onDrag={(e, d) => handleDrag(field, d)}
                  onDragStop={(e, d) => handleDragStop(field.id, d)}
                  onResize={(e, direction, ref, delta, position) =>
                    handleResize(field, ref, position)
                  }
                  onResizeStop={(e, direction, ref, delta, position) =>
                    handleResizeStop(field.id, ref, position)
                  }
                  onMouseDown={() => setSelectedId(field.id)}
                  className={
                    "group " +
                    (selectedId === field.id
                      ? "outline outline-2 outline-d-cat-btn"
                      : "outline outline-1 outline-gray-200")
                  }
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeField(field.id);
                    }}
                    className="hidden group-hover:flex absolute -top-3 -right-3 z-10 w-5 h-5 rounded-full bg-red-500 text-white items-center justify-center text-[10px]"
                    title={languageHelper("Delete")}
                  >
                    <FaTrash size={9} />
                  </button>
                  <FieldContent field={field} />
                </Rnd>
              ))}
            </div>
          </div>

          {/* Inspector */}
          <div className="w-full xl:w-64 shrink-0 border rounded-lg p-3 bg-gray-50">
            <h3 className="text-sm font-semibold text-d-bg-text mb-2">
              {languageHelper("Field Settings")}
            </h3>
            {!selectedField && (
              <p className="text-xs text-gray-500">
                {languageHelper("Select a field on the canvas to edit it")}
              </p>
            )}
            {selectedField && (
              <div className="space-y-3">
                <div className="text-xs text-gray-600">
                  {languageHelper(selectedField.label)}
                </div>

                {selectedField.type === FIELD_TYPES.TEXT && (
                  <label className="block">
                    <span className="text-xs text-d-bg-text font-semibold">
                      {languageHelper("Text")}
                    </span>
                    <textarea
                      rows={2}
                      className="w-full px-2 py-1 border rounded text-sm mt-1"
                      value={selectedField.text}
                      onChange={(e) =>
                        updateField(selectedField.id, { text: e.target.value })
                      }
                    />
                  </label>
                )}

                {[
                  FIELD_TYPES.TEXT,
                  FIELD_TYPES.DATA,
                  FIELD_TYPES.TOTALS,
                  FIELD_TYPES.ITEMS_TABLE,
                ].includes(selectedField.type) && (
                  <>
                    <label className="block">
                      <span className="text-xs text-d-bg-text font-semibold">
                        {languageHelper("Font Size")}
                      </span>
                      <input
                        type="number"
                        min={8}
                        max={40}
                        className="w-full px-2 py-1 border rounded text-sm mt-1"
                        value={selectedField.style?.fontSize || 12}
                        onChange={(e) =>
                          updateFieldStyle(selectedField.id, {
                            fontSize: Number(e.target.value),
                          })
                        }
                      />
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!selectedField.style?.bold}
                        onChange={(e) =>
                          updateFieldStyle(selectedField.id, {
                            bold: e.target.checked,
                          })
                        }
                      />
                      <span className="text-xs text-d-bg-text font-semibold">
                        {languageHelper("Bold")}
                      </span>
                    </label>

                    <label className="block">
                      <span className="text-xs text-d-bg-text font-semibold">
                        {languageHelper("Align")}
                      </span>
                      <select
                        className="w-full px-2 py-1 border rounded text-sm mt-1"
                        value={selectedField.style?.align || "left"}
                        onChange={(e) =>
                          updateFieldStyle(selectedField.id, {
                            align: e.target.value,
                          })
                        }
                      >
                        <option value="left">{languageHelper("Left")}</option>
                        <option value="center">{languageHelper("Center")}</option>
                        <option value="right">{languageHelper("Right")}</option>
                      </select>
                    </label>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => removeField(selectedField.id)}
                  className="w-full bg-red-500 text-white text-xs px-3 py-1.5 rounded shadow hover:shadow-lg"
                >
                  {languageHelper("Delete Field")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export default InvoiceBuilderEditor;
